import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import ukMoment from '../../../utils/ukTime';
import applyPinAffix from '../../../utils/pin';

import CancelBooking from "./CancelBooking.action";
import ClassBookingPreview from '../../experiences/account/components/BookingPreview';
import DataTable from '../../components/DataTable';
import Loading from '../../components/Loading';
import Meta from "../../components/Meta";
import Header from "../../components/Header";
import AppContext from "../../../context";

import Data from "../../../repository";
import ExperienceData from "../../experiences/Data";
import { getBaseUrl } from "../../../config/tenant";

import styles from '../../../styles/Bookings.module.scss';

const displayDate = date => ukMoment(date).format('DD MMM YYYY');
const displayTime = date => ukMoment(date).format('HH:mm');

const getStatus = booking => booking.cancelled ? 'Cancelled' : 'Active';

function Bookings() {
	const context = useContext(AppContext);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [bookings, setBookings] = useState([]);
	const [classBookings, setClassBookings] = useState([]);
	const [copiedId, setCopiedId] = useState(null);
	const [searchParams] = useSearchParams();
	const success = searchParams.get("success") === "true";

	const fetchBookings = useCallback(async () => {
		const result = await Data.fetchBookings();
		return result.data;
	}, []);

	const refreshBookings = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const [openGymBookings, classBookingsResult] = await Promise.all([
				fetchBookings(),
				ExperienceData.fetchAllExperienceBookings()
			]);
			setBookings(openGymBookings);
			setClassBookings(classBookingsResult.data || []);
		} catch (err) {
			setError(err?.response?.data?.message || 'Could not load bookings. Please try again.');
			setBookings([]);
			setClassBookings([]);
		} finally {
			setLoading(false);
		}
	}, [fetchBookings]);

	useEffect(() => {
		refreshBookings();
	}, [refreshBookings]);

	const copyInvite = useCallback((booking) => {
		navigator.clipboard.writeText(`${getBaseUrl()}/dashboard/book/join/${booking.shareLink}`);
		setCopiedId(booking._id);
	}, []);

	const columns = useMemo(() => [
		{
			id: 'date',
			header: 'Date',
			accessor: booking => booking.session.start,
			cell: booking => displayDate(booking.session.start)
		},
		{
			id: 'time',
			header: 'Time',
			accessor: booking => displayTime(booking.session.start),
			cell: booking => `${displayTime(booking.session.start)} - ${displayTime(booking.session.end)}`
		},
		{
			id: 'studio',
			header: 'Studio',
			accessor: booking => booking.session.Studio.name
		},
		{
			id: 'pin',
			header: 'PIN',
			accessor: booking => booking.access?.pin || '',
			cell: booking => booking.cancelled ? '-' : (applyPinAffix(booking.access?.pin, context.domain) || '-')
		},
		{
			id: 'status',
			header: 'Status',
			accessor: getStatus,
			cell: booking => <span className={`${styles.statusPill} ${booking.cancelled ? styles.cancelledPill : styles.activePill}`}>{getStatus(booking)}</span>
		},
		{
			id: 'actions',
			header: '',
			sortable: false,
			search: false,
			cell: booking => {
				if (booking.cancelled) return '-';
				const isInFuture = new Date() < new Date(booking.session.start);
				const cancellationWindowHours = context.domain?.cancellationWindowHours ?? 24;
				const withinCancellationWindow = isInFuture && (new Date(booking.session.start) < moment().add(cancellationWindowHours, 'hours').toDate());

				return (
					<div className={styles.tableActions}>
						<button type="button" className="btn btnEmpty btnSmall" onClick={() => copyInvite(booking)}>
							{copiedId === booking._id ? 'Copied' : 'Copy Invite'}
						</button>
						<CancelBooking id={booking._id} withinCancellationWindow={withinCancellationWindow} cancellationWindowHours={cancellationWindowHours} isInFuture={isInFuture} />
					</div>
				);
			}
		}
	], [copiedId, copyInvite, context.domain]);

	if (loading) return <Loading button={false} />;

	return (
		<>
			<Meta title="Manage Bookings" />

			<Header heading={"Manage Bookings"} />
			<main>
				<div className={styles.bookings}>
					{success && <div className={styles.success}><p>Your booking has been created.</p></div>}
					{error && (
						<div className={styles.error}>
							<p>{error}</p>
							<button type="button" className="btn btnEmpty" onClick={refreshBookings}>Try Again</button>
						</div>
					)}
					{!bookings.length && !classBookings.length && <div className={styles.none}><p>You haven't made any bookings yet. Create your first booking <Link to="/book">here</Link>.</p></div>}

					{bookings.length ? (
						<DataTable
							title="Open Gym Bookings"
							description="Review upcoming, past, shared, and cancelled open gym bookings."
							columns={columns}
							data={bookings}
							queryPrefix="openGymBookings"
							initialSort="date"
							initialDirection="desc"
							emptyMessage="You do not have any open gym bookings."
							rowClassName={booking => booking.cancelled ? styles.cancelledTableRow : ''}
						/>
					) : null}

					{classBookings.length ? <h2>Class Bookings</h2> : null}
					{classBookings.map((booking) => {
						return <ClassBookingPreview key={booking._id} booking={booking} />;
					})}
				</div>
			</main>
		</>
	);
}

export default Bookings;
